import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Groups,
  Close,
  Settings,
  InfoOutlined,
  CalendarToday,
  Add,
  Delete,
  CheckCircle,
  AccessTime,
  CalendarMonth,
} from "@mui/icons-material";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

import CustomInput from "../../components/CustomInput";
import PriorityEditor from "../../components/PriorityEditor";
import MemberWorkingHours from "../../components/MemberWorkingHours";
import NightShiftBadge from "../../components/NightShiftBadge";
import TimezoneConfig from "../../components/TimezoneConfig";
import { isNightShift } from "../../utils/calendarUtils";
import { useSystemTheme } from "context/SystemThemeContext";

const TeamManagementModal = React.memo(({ open, onClose, domainId, refreshData, initialTeamData }) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [teamMembers, setTeamMembers] = useState(initialTeamData || []);
  const [sharedCalendars, setSharedCalendars] = useState([]);
  const [config, setConfig] = useState(null);
  
  const [currentTimezone, setCurrentTimezone] = useState('UTC');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [timezones, setTimezones] = useState([]);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [isEditingTimezone, setIsEditingTimezone] = useState(false);
  const [timezonesLoaded, setTimezonesLoaded] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  
  const [newConfig, setNewConfig] = useState({
    slot_duration_minutes: 30,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    min_lead_time_hours: 4,
    max_booking_days: 60,
  });
  
  const [activeTab, setActiveTab] = useState('team-members');
  const [newMemberPriority, setNewMemberPriority] = useState(5);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  
  const [newMemberSettings, setNewMemberSettings] = useState({
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });

  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingMemberSettings, setEditingMemberSettings] = useState({});

  const priorityOptions = [
    { label: "High", value: 1 },
    { label: "Medium", value: 5 },
    { label: "Low", value: 10 },
  ];

  const fetchTimezone = useCallback(async () => {
    if (!domainId) return;
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/domain/timezone`, {
        headers: { "DOMAIN-TOKEN": domainId }
      });
      
      if (res.data.success) {
        const timezone = res.data.data.timezone || 'UTC';
        setCurrentTimezone(timezone);
        setSelectedTimezone(timezone);
      }
    } catch (err) {
      console.error("Failed to fetch timezone:", err);
      setCurrentTimezone('UTC');
      setSelectedTimezone('UTC');
    }
  }, [domainId]);

  const updateTimezone = useCallback(async () => {
    if (!domainId || !selectedTimezone) return;
    
    try {
      setSavingTimezone(true);
      const res = await axios.patch(
        `${FAST_API_BASE_URL}/api/domain/timezone`,
        { timezone: selectedTimezone },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setCurrentTimezone(selectedTimezone);
        setIsEditingTimezone(false);
        setTimezoneSearch('');
        setSuccess("Timezone updated successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to update timezone");
      }
    } catch (err) {
      console.error("Failed to update timezone:", err);
      setError("Failed to update timezone. Please try again.");
    } finally {
      setSavingTimezone(false);
    }
  }, [domainId, selectedTimezone]);

  useEffect(() => {
    const loadTimezones = () => {
      if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
        try {
          const supportedTimezones = Intl.supportedValuesOf("timeZone");
          const allTimezones = ['UTC', ...supportedTimezones.filter(tz => tz !== 'UTC')];
          setTimezones(allTimezones);
          setTimezonesLoaded(true);
        } catch (err) {
          console.error("Failed to get supported timezones:", err);
          setFallbackTimezones();
        }
      } else {
        setFallbackTimezones();
      }
    };

    const setFallbackTimezones = () => {
      const fallbackTimezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Toronto',
        'America/Vancouver',
        'America/Sao_Paulo',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Madrid',
        'Europe/Rome',
        'Europe/Amsterdam',
        'Asia/Tokyo',
        'Asia/Shanghai',
        'Asia/Singapore',
        'Asia/Hong_Kong',
        'Asia/Seoul',
        'Asia/Dubai',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Pacific/Auckland',
        'Africa/Cairo',
        'Africa/Johannesburg',
      ];
      setTimezones(fallbackTimezones);
      setTimezonesLoaded(true);
    };

    loadTimezones();
  }, []);

  useEffect(() => {
    if (timezonesLoaded && currentTimezone && !selectedTimezone) {
      const exists = timezones.some(tz => tz === currentTimezone);
      setSelectedTimezone(exists ? currentTimezone : timezones[0] || 'UTC');
    }
  }, [timezonesLoaded, currentTimezone, timezones, selectedTimezone]);

  useEffect(() => {
    if (!open || !domainId) return;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await fetchTimezone();
        
        const calendarsRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/writable-calendars`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: { calendars: [] } } }));

        if (calendarsRes.data.success) {
          const calendars = calendarsRes.data.data.calendars || [];
          const processedCalendars = calendars.map(calendar => ({
            ...calendar,
            id: calendar.id || calendar.email,
            name: calendar.name || calendar.summary || calendar.email
          }));
          setSharedCalendars(processedCalendars);
        }

        const statusRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/status`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: {} } }));

        if (statusRes.data.success) {
          const statusData = statusRes.data.data;
          
          if (statusData.team_members && statusData.team_members.length > 0) {
            setTeamMembers(statusData.team_members);
          } else if (initialTeamData) {
            setTeamMembers(initialTeamData);
          }
          
          if (statusData.config) {
            const configData = statusData.config;
            setConfig(configData);
            setNewConfig({
              slot_duration_minutes: configData.slot_duration_minutes || 30,
              buffer_before_minutes: configData.buffer_before_minutes || 5,
              buffer_after_minutes: configData.buffer_after_minutes || 5,
              min_lead_time_hours: configData.min_lead_time_hours || 4,
              max_booking_days: configData.max_booking_days || 60,
            });
          }
        }

      } catch (err) {
        console.error("Failed to fetch team data:", err);
        setError("Failed to load team data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, domainId, initialTeamData, fetchTimezone]);

  const getPriorityLabel = (value) => {
    const option = priorityOptions.find(opt => opt.value === value);
    return option ? option.label : "Medium";
  };

  const getPriorityColor = (value) => {
    switch(value) {
      case 1: return "#d32f2f";
      case 5: return "#ed6c02";
      case 10: return "#2e7d32";
      default: return "#666666";
    }
  };

  const handleAddTeamMember = async () => {
    if (!selectedCalendar) {
      setError("Please select a calendar first");
      return;
    }
    
    const calendar = availableCalendars.find(c => 
      c.id === selectedCalendar || 
      c.email === selectedCalendar ||
      String(c.id) === String(selectedCalendar)
    );
    
    if (!calendar) {
      setError("Selected calendar not found. Please select again.");
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          enabled: true,
          working_hours_start: newMemberSettings.working_hours_start,
          working_hours_end: newMemberSettings.working_hours_end,
          working_days: newMemberSettings.working_days,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        const newMember = {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          email: calendar.email,
          enabled: true,
          working_hours_start: newMemberSettings.working_hours_start,
          working_hours_end: newMemberSettings.working_hours_end,
          working_days: newMemberSettings.working_days,
          is_night_shift: isNightShift(newMemberSettings.working_hours_start, newMemberSettings.working_hours_end),
        };
        
        setTeamMembers(prev => [...prev, newMember]);
        setSelectedCalendar('');
        setNewMemberSettings({
          working_hours_start: '09:00',
          working_hours_end: '17:00',
          working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        });
        
        setSuccess("Team member added successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to add team member");
      }
    } catch (err) {
      console.error("Failed to add team member:", err);
      const errorMsg = err.response?.data?.message || "Failed to add team member. Please try again.";
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMemberSettings = async (memberId) => {
    const member = teamMembers.find(m => m.calendar_id === memberId);
    if (!member || !editingMemberSettings[memberId]) return;
    
    try {
      setSaving(true);
      const settings = editingMemberSettings[memberId];
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: memberId,
          priority: member.priority,
          name: member.name,
          enabled: true,
          working_hours_start: settings.working_hours_start,
          working_hours_end: settings.working_hours_end,
          working_days: settings.working_days,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.map(m => 
          m.calendar_id === memberId ? { 
            ...m, 
            ...settings,
            is_night_shift: isNightShift(settings.working_hours_start, settings.working_hours_end)
          } : m
        ));
        setEditingMemberId(null);
        setEditingMemberSettings({});
        setSuccess("Member settings updated successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to update member settings");
      }
    } catch (err) {
      console.error("Failed to update member settings:", err);
      setError("Failed to update member settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePriority = async (calendarId, priority) => {
    try {
      setSaving(true);
      const member = teamMembers.find(m => m.calendar_id === calendarId);
      if (!member) return;
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          priority: priority,
          name: member.name,
          enabled: true,
          working_hours_start: member.working_hours_start || '09:00',
          working_hours_end: member.working_hours_end || '17:00',
          working_days: member.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.map(member => 
          member.calendar_id === calendarId ? { ...member, priority } : member
        ));
        setSuccess("Priority updated successfully!");
      } else {
        setError(res.data.message || "Failed to update priority");
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
      setError("Failed to update priority. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTeamMember = async (calendarId) => {
    if (!calendarId) return;
    
    try {
      setSaving(true);
      const member = teamMembers.find(m => m.calendar_id === calendarId);
      if (!member) return;
      
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          priority: member.priority,
          name: member.name,
          enabled: false,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.filter(member => member.calendar_id !== calendarId));
        setSuccess("Team member removed");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to remove team member");
      }
    } catch (err) {
      console.error("Failed to remove team member:", err);
      setError("Failed to remove team member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const startEditingMember = (member) => {
    setEditingMemberId(member.calendar_id);
    setEditingMemberSettings({
      [member.calendar_id]: {
        working_hours_start: member.working_hours_start || '09:00',
        working_hours_end: member.working_hours_end || '17:00',
        working_days: member.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      }
    });
  };

  const cancelEditingMember = () => {
    setEditingMemberId(null);
    setEditingMemberSettings({});
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        { config: newConfig },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setConfig(newConfig);
        setSuccess("Configuration saved successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Failed to save config:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const availableCalendars = useMemo(() => {
    return sharedCalendars.filter(calendar => {
      const isAlreadyMember = teamMembers.some(member => {
        const memberIdentifiers = [member.calendar_id, member.email, member.id].filter(Boolean);
        const calendarIdentifiers = [calendar.id, calendar.email, calendar.summary].filter(Boolean);
        return memberIdentifiers.some(memberId => 
          calendarIdentifiers.some(calendarId => 
            memberId && calendarId && memberId.toLowerCase() === calendarId.toLowerCase()
          )
        );
      });
      return !isAlreadyMember;
    });
  }, [sharedCalendars, teamMembers]);

  const TimezoneDisplay = () => {
    return (
      <TimezoneConfig 
        timezone={selectedTimezone}
        onTimezoneChange={setSelectedTimezone}
        timezones={timezones}
        timezonesLoaded={timezonesLoaded}
        currentTimezone={currentTimezone}
        isEditing={isEditingTimezone}
        setIsEditing={setIsEditingTimezone}
        saving={saving || savingTimezone}
        domainId={domainId}
        onTimezoneSave={updateTimezone}
      />
    );
  };

  if (loading) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDarkMode ? '#060C18' : '#fff',
            boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.08)',
          }
        }}
      >
        <DialogContent sx={{ p: 6 }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2}>
            <CircularProgress sx={{ color: "#219297" }} size={48} />
            <Typography variant="body1" sx={{ color: isDarkMode ? '#a0aec0' : "#666", fontWeight: 400 }}>
              Loading team data...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: isDarkMode ? '#060C18' : '#fff',
          boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.3)' : '0 8px 32px rgba(0,0,0,0.08)',
          maxHeight: '90vh',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        pb: 2,
        borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}`,
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: '#21929715',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Groups sx={{ color: '#219297', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#333', fontSize: '1.1rem' }}>
                Team Management
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666' }}>
                Manage team members and scheduling settings
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: isDarkMode ? '#2d2d3f' : '#f5f5f5'
              }
            }}
          >
            <Close sx={{ color: isDarkMode ? '#e2e8f0' : undefined }} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ 
          borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}`,
          px: 3,
          pt: 2
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={activeTab === 'team-members' ? 'contained' : 'text'}
              onClick={() => setActiveTab('team-members')}
              startIcon={<Groups />}
              sx={{ 
                borderRadius: '8px 8px 0 0',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 2,
                py: 1,
                backgroundColor: activeTab === 'team-members' ? '#219297' : 'transparent',
                color: activeTab === 'team-members' ? '#fff' : (isDarkMode ? '#a0aec0' : '#666'),
                '&:hover': {
                  backgroundColor: activeTab === 'team-members' ? '#197f85' : (isDarkMode ? '#2d2d3f' : '#f5f5f5'),
                  color: activeTab === 'team-members' ? '#fff' : '#219297',
                }
              }}
            >
              Team Members
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'contained' : 'text'}
              onClick={() => setActiveTab('settings')}
              startIcon={<Settings />}
              sx={{ 
                borderRadius: '8px 8px 0 0',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 2,
                py: 1,
                backgroundColor: activeTab === 'settings' ? '#219297' : 'transparent',
                color: activeTab === 'settings' ? '#fff' : (isDarkMode ? '#a0aec0' : '#666'),
                '&:hover': {
                  backgroundColor: activeTab === 'settings' ? '#197f85' : (isDarkMode ? '#2d2d3f' : '#f5f5f5'),
                  color: activeTab === 'settings' ? '#fff' : '#219297',
                }
              }}
            >
              Global Settings
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ px: 3, pt: 2 }}>
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                borderRadius: '10px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #c8e6c9',
                color: '#2e7d32',
                '& .MuiAlert-icon': {
                  color: '#2e7d32'
                }
              }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: '10px',
                backgroundColor: '#ffebee',
                border: '1px solid #ef9a9a',
                color: '#d32f2f',
                '& .MuiAlert-icon': {
                  color: '#d32f2f'
                }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Box>

        <Box sx={{ px: 3, py: 2, minHeight: 400, maxHeight: 'calc(80vh - 200px)', overflowY: 'auto' }}>
          <TimezoneDisplay />
          
          {activeTab === 'team-members' && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#333', fontSize: '0.95rem' }}>
                    Current Team Members
                  </Typography>
                  <Chip
                    label={`${teamMembers.length} members`}
                    size="small"
                    sx={{
                      backgroundColor: '#21929715',
                      color: '#219297',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
                
                {teamMembers.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {teamMembers.map((member, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}`,
                          backgroundColor: isDarkMode ? '#0a1020' : '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            borderColor: '#21929730',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: member.is_night_shift ? '#4527a0' : '#219297',
                                fontSize: '0.9rem',
                                fontWeight: 600
                              }}
                            >
                              {member.name?.[0]?.toUpperCase() || 'T'}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#333' }}>
                                  {member.name || member.email || member.calendar_id}
                                </Typography>
                                {member.is_night_shift && <NightShiftBadge isNightShift={true} size="small" />}
                              </Box>
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', display: 'block', mb: 1 }}>
                                {member.email || member.calendar_id}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <PriorityEditor 
                                  member={member}
                                  saving={saving}
                                  onUpdatePriority={handleUpdatePriority}
                                />
                              </Box>
                              
                              <MemberWorkingHours
                                member={member}
                                editingMemberId={editingMemberId}
                                editingMemberSettings={editingMemberSettings}
                                setEditingMemberSettings={setEditingMemberSettings}
                                onUpdateMemberSettings={handleUpdateMemberSettings}
                                onStartEditing={startEditingMember}
                                onCancelEditing={cancelEditingMember}
                                saving={saving}
                              />
                            </Box>
                          </Box>
                          
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (window.confirm(`Remove ${member.name || member.email || member.calendar_id}?`)) {
                                handleRemoveTeamMember(member.calendar_id || member.email);
                              }
                            }}
                            sx={{
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: '#ffebee'
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Paper sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: `1px dashed ${isDarkMode ? '#4a5568' : '#e0e0e0'}`,
                    backgroundColor: isDarkMode ? '#0a1020' : '#fafafa'
                  }}>
                    <Groups sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#a0aec0' : '#999' }}>
                      No team members yet. Add members below.
                    </Typography>
                  </Paper>
                )}
              </Box>

              <Divider sx={{ my: 3, borderColor: isDarkMode ? '#2d2d3f' : undefined }} />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#333', mb: 2, fontSize: '0.95rem' }}>
                  Add Team Member
                </Typography>
                
                {availableCalendars.length > 0 ? (
                  <Paper sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}` }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                            Select Calendar
                          </Typography>
                          <Select
                            value={selectedCalendar}
                            onChange={(e) => setSelectedCalendar(e.target.value)}
                            displayEmpty
                            sx={{
                              borderRadius: '8px',
                              backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
                              color: isDarkMode ? '#e2e8f0' : undefined,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDarkMode ? '#4a5568' : '#e0e0e0'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#219297'
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em style={{ color: isDarkMode ? '#a0aec0' : '#999' }}>Select a calendar...</em>
                            </MenuItem>
                            {availableCalendars.map((calendar) => {
                              const calendarValue = calendar.id || calendar.email;
                              if (!calendarValue) return null;
                              
                              return (
                                <MenuItem key={calendarValue} value={calendarValue}>
                                  <Box display="flex" alignItems="center" gap={1.5}>
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#21929715' }}>
                                      <CalendarToday sx={{ fontSize: 16, color: '#219297' }} />
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: isDarkMode ? '#e2e8f0' : undefined }}>
                                        {calendar?.name || calendar?.summary || calendar?.email || calendar?.id}
                                      </Typography>
                                      {calendar?.email && (
                                        <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : 'textSecondary' }}>
                                          {calendar.email}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            }).filter(Boolean)}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Priority Level
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={newMemberPriority}
                            onChange={(e) => setNewMemberPriority(e.target.value)}
                            sx={{
                              borderRadius: '8px',
                              backgroundColor: isDarkMode ? '#2d2d3f' : '#fff',
                              color: isDarkMode ? '#e2e8f0' : undefined,
                            }}
                          >
                            {priorityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: getPriorityColor(option.value),
                                    }}
                                  />
                                  <Typography variant="body2">{option.label}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Working Hours
                        </Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          <CustomInput
                            type="time"
                            value={newMemberSettings.working_hours_start}
                            onChange={(e) => setNewMemberSettings(prev => ({
                              ...prev,
                              working_hours_start: e.target.value
                            }))}
                            style={{ flex: 1 }}
                          />
                          <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666' }}>to</Typography>
                          <CustomInput
                            type="time"
                            value={newMemberSettings.working_hours_end}
                            onChange={(e) => setNewMemberSettings(prev => ({
                              ...prev,
                              working_hours_end: e.target.value
                            }))}
                            style={{ flex: 1 }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                          Working Days
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                            <Chip
                              key={day}
                              label={day.slice(0, 3).toUpperCase()}
                              size="small"
                              onClick={() => {
                                const days = [...newMemberSettings.working_days];
                                if (days.includes(day)) {
                                  setNewMemberSettings(prev => ({
                                    ...prev,
                                    working_days: days.filter(d => d !== day)
                                  }));
                                } else {
                                  setNewMemberSettings(prev => ({
                                    ...prev,
                                    working_days: [...days, day]
                                  }));
                                }
                              }}
                              sx={{
                                fontSize: '0.7rem',
                                height: '24px',
                                cursor: 'pointer',
                                backgroundColor: newMemberSettings.working_days.includes(day) 
                                  ? '#219297' 
                                  : (isDarkMode ? '#2d2d3f' : '#e0e0e0'),
                                color: newMemberSettings.working_days.includes(day) 
                                  ? '#fff' 
                                  : (isDarkMode ? '#a0aec0' : '#666'),
                                '&:hover': {
                                  backgroundColor: newMemberSettings.working_days.includes(day) 
                                    ? '#197f85' 
                                    : (isDarkMode ? '#3d3d4f' : '#d0d0d0'),
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleAddTeamMember}
                          disabled={!selectedCalendar || saving}
                          startIcon={saving ? <CircularProgress size={16} /> : <Add />}
                          sx={{
                            mt: 1,
                            backgroundColor: '#219297',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            color: '#ffffff',
                            py: 1.2,
                            '&:hover': {
                              backgroundColor: '#197f85'
                            },
                            '&.Mui-disabled': {
                              backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                              color: isDarkMode ? '#a0aec0' : '#fdfdfdff'
                            }
                          }}
                        >
                          {saving ? 'Adding Member...' : 'Add Team Member'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : (
                  <Paper sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    borderRadius: '12px',
                    border: `1px dashed ${isDarkMode ? '#4a5568' : '#e0e0e0'}`
                  }}>
                    <InfoOutlined sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#a0aec0' : '#999' }}>
                      No additional calendars available. Share calendars with your account to add team members.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          )}

          {activeTab === 'settings' && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#333', mb: 2, fontSize: '0.95rem' }}>
                Global Scheduling Configuration
              </Typography>
              
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  borderRadius: '10px',
                  backgroundColor: isDarkMode ? '#0d1425' : '#e3f2fd',
                  border: `1px solid ${isDarkMode ? '#2d2d3f' : '#90caf9'}`,
                  color: isDarkMode ? '#e2e8f0' : undefined,
                }}
              >
                <Typography variant="caption">
                  These are global defaults. Individual team members can override with their own working hours.
                </Typography>
              </Alert>

              <Paper sx={{ 
                p: 2.5, 
                borderRadius: '12px', 
                border: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}`,
                backgroundColor: isDarkMode ? '#0a1020' : '#fff',
              }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                      Slot Duration
                    </Typography>
                    <CustomInput
                      type="number"
                      value={newConfig.slot_duration_minutes}
                      onChange={(e) => setNewConfig({
                        ...newConfig,
                        slot_duration_minutes: parseInt(e.target.value) || 30
                      })}
                      placeholder="Minutes"
                      startIcon={<AccessTime sx={{ fontSize: 18 }} />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                      Maximum Booking Days
                    </Typography>
                    <CustomInput
                      type="number"
                      value={newConfig.max_booking_days}
                      onChange={(e) => setNewConfig({
                        ...newConfig,
                        max_booking_days: parseInt(e.target.value) || 90
                      })}
                      placeholder="Days"
                      startIcon={<CalendarMonth sx={{ fontSize: 18 }} />}
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveConfig}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      backgroundColor: '#219297',
                      color: '#ffffff',
                      px: 3,
                      '&:hover': {
                        backgroundColor: '#197f85'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                        color: isDarkMode ? '#a0aec0' : '#999',
                      }
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        pt: 2,
        borderTop: `1px solid ${isDarkMode ? '#2d2d3f' : '#f0f0f0'}`,
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: isDarkMode ? '#a0aec0' : '#666',
            px: 3,
            '&:hover': {
              backgroundColor: isDarkMode ? '#2d2d3f' : '#f5f5f5'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default TeamManagementModal;